import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { AlertCircle, Link2, UserPlus, Loader2 } from "lucide-react";

export interface QBCustomerMatch {
  id: string;
  displayName: string;
  companyName?: string;
  email?: string;
  phone?: string;
  matchType: 'exact' | 'strong' | 'partial';
  matchReasons?: string[];
  score?: number;
}

export interface UnlinkedAccountWithMatches {
  accountId: number;
  accountName: string;
  matches: QBCustomerMatch[];
}

export interface LinkDecision {
  accountId: number;
  decision: 'link' | 'create_new';
  qbCustomerId?: string;
}

interface QBCustomerLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unlinkedAccounts: UnlinkedAccountWithMatches[];
  onConfirm: (decisions: LinkDecision[]) => void;
  isProcessing?: boolean;
}

export function QBCustomerLinkDialog({
  open,
  onOpenChange,
  unlinkedAccounts,
  onConfirm,
  isProcessing = false,
}: QBCustomerLinkDialogProps) {
  const [decisions, setDecisions] = useState<Record<number, { decision: 'link' | 'create_new'; qbCustomerId?: string }>>({});

  const handleDecisionChange = (accountId: number, value: string) => {
    if (value === 'create_new') {
      setDecisions(prev => ({
        ...prev,
        [accountId]: { decision: 'create_new' }
      }));
    } else {
      setDecisions(prev => ({
        ...prev,
        [accountId]: { decision: 'link', qbCustomerId: value }
      }));
    }
  };

  const handleConfirm = () => {
    const finalDecisions: LinkDecision[] = unlinkedAccounts.map(account => {
      const decision = decisions[account.accountId];
      if (decision?.decision === 'link' && decision.qbCustomerId) {
        return {
          accountId: account.accountId,
          decision: 'link' as const,
          qbCustomerId: decision.qbCustomerId
        };
      }
      return {
        accountId: account.accountId,
        decision: 'create_new' as const
      };
    });
    onConfirm(finalDecisions);
  };

  const allDecisionsMade = unlinkedAccounts.every(account => decisions[account.accountId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            QuickBooks Customer Matching
          </DialogTitle>
          <DialogDescription>
            Some customers don't have a QuickBooks ID yet. We found potential matches - please review before sending invoices.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {unlinkedAccounts.map((account) => (
            <div key={account.accountId} className="border rounded-lg p-4 space-y-3">
              <div className="font-medium text-lg">{account.accountName}</div>
              
              <RadioGroup
                value={decisions[account.accountId]?.decision === 'link' 
                  ? decisions[account.accountId].qbCustomerId 
                  : decisions[account.accountId]?.decision === 'create_new' 
                    ? 'create_new' 
                    : undefined}
                onValueChange={(value) => handleDecisionChange(account.accountId, value)}
              >
                <div>
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Create new in QuickBooks</p>
                  <div className="flex items-center space-x-3 p-3 border-2 border-dashed border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50/30 transition-colors">
                    <RadioGroupItem value="create_new" id={`create-new-${account.accountId}`} />
                    <Label 
                      htmlFor={`create-new-${account.accountId}`}
                      className="flex-1 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <UserPlus className="h-4 w-4 text-green-600" />
                        <span className="font-medium">Create new customer</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Create a new QuickBooks customer and link automatically.
                      </p>
                    </Label>
                  </div>
                </div>

                {account.matches.length > 0 && (
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs font-medium text-gray-400 uppercase">or</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                )}

                {account.matches.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Link to existing customer</p>
                    {account.matches.map((match) => (
                      <div 
                        key={match.id} 
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <RadioGroupItem value={match.id} id={`match-${account.accountId}-${match.id}`} />
                        <Label 
                          htmlFor={`match-${account.accountId}-${match.id}`}
                          className="flex-1 cursor-pointer"
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link2 className="h-4 w-4 text-blue-600" />
                            <span className="font-medium">{match.displayName}</span>
                            {match.matchReasons && match.matchReasons.length > 0 ? (
                              match.matchReasons.map((reason, idx) => (
                                <Badge 
                                  key={idx}
                                  variant="outline" 
                                  className={reason === 'phone match' 
                                    ? 'bg-green-50 text-green-700 border-green-200' 
                                    : reason === 'email match'
                                      ? 'bg-purple-50 text-purple-700 border-purple-200'
                                      : reason === 'exact name'
                                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                                        : 'bg-yellow-50 text-yellow-700 border-yellow-200'}
                                >
                                  {reason}
                                </Badge>
                              ))
                            ) : (
                              <Badge 
                                variant="outline" 
                                className={match.matchType === 'exact' 
                                  ? 'bg-green-50 text-green-700 border-green-200' 
                                  : match.matchType === 'strong'
                                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                                    : 'bg-yellow-50 text-yellow-700 border-yellow-200'}
                              >
                                {match.matchType === 'exact' ? 'best match' : match.matchType === 'strong' ? 'likely match' : 'possible'}
                              </Badge>
                            )}
                          </div>
                          {(match.companyName || match.email || match.phone) && (
                            <div className="text-sm text-gray-500 mt-1">
                              {match.companyName && <span>{match.companyName}</span>}
                              {match.companyName && (match.email || match.phone) && <span> · </span>}
                              {match.phone && <span>{match.phone}</span>}
                              {match.phone && match.email && <span> · </span>}
                              {match.email && <span>{match.email}</span>}
                            </div>
                          )}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              </RadioGroup>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isProcessing}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm}
            disabled={!allDecisionsMade || isProcessing}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              'Confirm & Send Invoices'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
